import { useReducer, useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from "react-i18next";
import { Dropdown } from "bootstrap";
import { useLocation } from '@reach/router'

import Editor from '../../Editor/Editor'
import Treatment from './Treatment'
import Diagnostic from './Diagnostic'
import VitalSigns from './VitalSigns'
import Prescription from '../Prescription/Prescription'
import Files from './Files'

import useMutation from "hooks/useMutation";
import getDate from 'lib/getDate'
import { navigate } from '@reach/router'
import { Modal } from 'react-bootstrap'

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_RECORD': {
      return {
        changed: Date.now(),
        saving: Date.now(),
        error: undefined,
        newRecord: undefined,
        record: { ...action.record }
      }
    }
    case 'SAVING': {
      return {
        ...state,
        saving: Date.now(),
        error: undefined,
        newRecord: undefined,
      }
    }
    case 'SAVED': {
      const hasNewerChanges = state.saving > action.saving
      return {
        ...state,
        record: { ...action.record, ...state.record },
        saving: hasNewerChanges ? state.saving : false,
        changed: hasNewerChanges ? state.changed : undefined,
        error: undefined,
        newRecord: undefined,
      }
    }
    case 'SAVE_ERROR': {
      return {
        ...state,
        error: action.error,
        saving: undefined,
        newRecord: undefined,
      }
    }
    case 'NEW_RECORD': {
      return {
        record: action.record,
        changed: undefined,
        saving: undefined,
        error: undefined,
        newRecord: true
      }
    }
    default: {
      throw Error(`Unknown action: ${action.type}`)
    }
  }
}

// Bug real (Trello "V2 | Arreglos de Responsive Design", 2026-09-04): este
// modal (solo un título + una línea de texto + un botón) ocupaba 100% del
// ancho y 90% del alto de la pantalla. Dos causas reales, no una:
// 1. `dialogClassName='modal-fullscreen-sm-down'` fuerza pantalla completa
//    por debajo de 576px (mobile) — total overkill para tan poco contenido;
//    se saca, dejando que el tamaño lo defina `size="md"` (max-width:500px)
//    en todos los anchos, con los márgenes/centrado horizontal normales de
//    Bootstrap incluso en mobile.
// 2. `style={{maxHeight:"80vh"}}` en el <Modal> de react-bootstrap es
//    no-op: react-bootstrap aplica ese `style` al wrapper .modal (el overlay
//    fixed de pantalla completa), NUNCA al .modal-dialog visible — no
//    cumplía su propio objetivo de acotar el alto.
// El prop `centered` (nativo de react-bootstrap, agrega
// .modal-dialog-centered) resuelve el pedido de centrado vertical sin CSS
// propio.
const SavingModal = ({ show, onHide, onConfirm, confirmLabel, closeStyle, isSaving, t }) => {
  return (
    <Modal show={show} size="md" centered onHide={onHide} dialogClassName='font-nunito'>
      <Modal.Header className='border-0 d-flex justify-content-between align-items-top'>
        <h5 className="modal-title">{t('ehr:record_is_saving', "Esta evolución tiene cambios en proceso de guardado")}</h5>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Close"
          style={closeStyle}
        ></button>
      </Modal.Header>
      <Modal.Body className='border-0'>
        <div>
          {t('ehr:record_saving_wait_message', "Esperá un momento hasta que el proceso se complete.")}
        </div>
      </Modal.Body>
      <Modal.Footer className='border-0'>
        <div
          className={`text-center mx-3 ${isSaving ? '' : 'text-success'}`}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {t('ehr:record_is_saving', 'Guardando...')}
            </>
          ) : (
            t('ehr:saved', "Guardado")
          )}
        </div>
        <button className="btn btn-secondary" onClick={onConfirm} disabled={isSaving}>
          {confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

// [Hallazgo real, loop de mejora continua Ronda 4] el resumen de IA llega
// como texto plano con \n\n entre secciones (MOTIVO/HALLAZGOS/INDICACIONES,
// ver server.js en meet) — nunca como HTML con <p> reales, a diferencia de
// una evolución escrita a mano (que siempre sale de este mismo Editor, ya en
// HTML). TinyMCE renderiza su contenido dentro de un <iframe> con su PROPIA
// hoja de estilos — el fix de white-space:pre-line del lado de solo lectura
// (Consultation.js, styles.module.scss) no llega a aplicarse ahí. Sin
// convertir a párrafos reales ANTES de cargarlo en el editor, las 3
// secciones se ven (y se guardarían así si el profesional edita sin
// tocarlas) como un solo bloque corrido.
const looksLikeHtml = text => /<[a-z][\s\S]*>/i.test(text)
const escapeHtmlText = text => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const plainTextToParagraphs = text => text
  .split(/\n{2,}/)
  .filter(block => block.trim())
  .map(block => `<p>${escapeHtmlText(block).replace(/\n/g, '<br>')}</p>`)
  .join('')

function Form(props) {
  const location = useLocation()
  const aiSummaryContent = props.record?.aiGenerated && props.record?.content && !looksLikeHtml(props.record.content)
    ? plainTextToParagraphs(props.record.content)
    : undefined
  const initialRecord = {
    consumer: { id: props.consumer?.id, label: props.consumer?.label },
    createdBy: props.user?.uid,
    createdByName: props.resource?.label || props.user?.name,
    createdByResourceID: props.resource?.id,
    type: 'records',
    ...props.record,
    ...(aiSummaryContent !== undefined ? { content: aiSummaryContent } : {}),
    date: props.record?.date || getDate()
  }
  const isInitialSaved = !!props.record?.id
  const [state, dispatch] = useReducer(reducer, {
    record: initialRecord,
    changed: undefined,
    saving: isInitialSaved ? false : undefined,
    error: undefined,
    newRecord: undefined
  })
  const [show, setShow] = useState({})
  const { t } = useTranslation();
  const [mutate, mutation] = useMutation(window._l(`/records`).substr(1));

  const $files = useRef(null)
  const [editorKey, setEditorKey] = useState(0)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [showErrorSpinner, setShowErrorSpinner] = useState(false)
  const [showNavigationModal, setShowNavigationModal] = useState(false)
  const [showNewEvolutionModal, setShowNewEvolutionModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const [pendingActionAfterSave, setPendingActionAfterSave] = useState(null)
  // Bug real (QA, Juan Cruz, 2026-08-20): sin esto, apenas la mutation de
  // "Guardar y aprobar" resolvía, `isUnreviewedAiSummary` (más abajo) volvía
  // a salir true en el siguiente render — sigue leyendo `props.record`
  // (nunca actualizado por el padre tras este guardado puntual) y, aparte,
  // el merge del reducer en 'SAVED' deja ganar el `reviewed:false` viejo de
  // `state.record` por sobre el `reviewed:true` fresco del server. El botón
  // volvía a "Guardar y aprobar" como si nunca se hubiera guardado nada.
  const [approved, setApproved] = useState(false)

  const hasUnsavedChanges = mutation.isLoading || mutation.error || state.error || (state.changed && state.saving !== false);

  const handleNavigationConfirm = useCallback((onConfirm) => {
    const hasError = state.error || mutation.error
    if (hasError && !state.saving) {
      setPendingNavigation(() => onConfirm)
      setShowErrorModal(true)
    } else {
      setPendingNavigation(() => onConfirm)
      setShowNavigationModal(true)
    }
  }, [state.error, mutation.error])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const handleClick = (event) => {
      const target = event.target.closest("a");
      if (target && target.href) {
        const targetUrl = new URL(target.href, window.location.origin);
        const currentUrl = new URL(window.location.href);

        if (targetUrl.pathname !== currentUrl.pathname) {
          event.preventDefault();
          handleNavigationConfirm(() => {
            window.location.href = target.href;
          });
        }
      }
    };

    const originalNavigate = navigate;
    const customNavigate = (to, options) => {
      handleNavigationConfirm(() => {
        originalNavigate(to, options);
      });
    };

    window.navigate = customNavigate;

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      window.navigate = originalNavigate;
    };
  }, [hasUnsavedChanges, handleNavigationConfirm, t]);

  // [Hallazgo real, pedido de Nacho 2026-08-12] para un resumen de IA sin
  // revisar todavía, "editar" NO debe implicar "guardar" — antes, el
  // auto-save de más abajo (disparado por CUALQUIER cambio, incluida la
  // conversión automática de texto plano a párrafos al montar el editor, ver
  // aiSummaryContent arriba) terminaba aprobando el borrador sin que el
  // profesional decidiera nada a propósito. Ahora el guardado de este caso
  // puntual es 100% explícito, vía el botón "Guardar y aprobar" de más abajo
  // — nunca por inactividad. El resto de los registros (evoluciones escritas
  // a mano, diagnósticos, etc.) sigue con el auto-save de siempre, sin
  // cambios.
  const isUnreviewedAiSummary = props.record?.aiGenerated && !props.record?.reviewed && !approved

  useEffect(() => {
    let ticker
    if (state.changed && !state.newRecord && !isUnreviewedAiSummary) {
      ticker = setTimeout(save, 2000)
    }
    return () => clearTimeout(ticker)
  }, [state.changed, isUnreviewedAiSummary])

  useEffect(() => {
    let show = e => {
      let key = e.target.dataset.form
      if (!key) return
      setShow(show => ({ ...show, [key]: true }))
    }

    let hide = e => {
      let key = e.target.dataset.form || e.form
      if (!key) return
      setShow(show => ({ ...show, [key]: false }))
    }

    window.document.body.addEventListener('show.bs.dropdown', show)
    window.document.body.addEventListener('hidden.bs.dropdown', hide)

    return () => {
      window.document.body.removeEventListener('show.bs.dropdown', show)
      window.document.body.removeEventListener('hidden.bs.dropdown', hide)
    }
  }, [])

  useEffect(() => {
    if (state.error) {
      if (showNavigationModal || showNewEvolutionModal) {
        setShowNavigationModal(false)
        setShowNewEvolutionModal(false)
        setShowErrorModal(true)
      }
      setShowErrorSpinner(true)
      const timer = setTimeout(() => {
        setShowErrorSpinner(false)
      }, 200)
      return () => clearTimeout(timer)
    } else {
      setShowErrorSpinner(false)
    }
  }, [state.error])

  useEffect(() => {
    const isSaving = mutation.isLoading || (typeof state.saving === 'number')
    const isSaved = state.saving === false && !mutation.isLoading

    if (pendingActionAfterSave && !isSaving && isSaved) {
      const action = pendingActionAfterSave
      setPendingActionAfterSave(null)
      action()
    }

    if (pendingActionAfterSave && state.error) {
      setPendingActionAfterSave(null)
    }

  }, [mutation.isLoading, state.saving, state.error, pendingActionAfterSave])

  const toggle = key => {
    setShow({ ...show, [key]: !show[key] })
  }

  const onEditorChange = content => {
    if (state.newRecord && !content.trim()) {
      return;
    }

    const record = { ...state.record, content }
    dispatch({ type: 'SET_RECORD', record })
  }

  const onChangeDate = ({ target: { value } }) => {
    const record = { ...state.record, date: value }
    dispatch({ type: 'SET_RECORD', record })
  }

  const onClose = key => {
    try {
      let $element = window.document.querySelector(`[data-form="${key}"]`)
      if ($element) Dropdown.getInstance($element).hide();
    } catch (e) {
      console.log(e)
    }
  }

  const onSave = key => record => {
    onClose(key)
    if (props.onSave && record) props.onSave([record])
  }

  // extraFields: solo lo usa el botón "Guardar y aprobar" de más abajo, para
  // mandar approveAiSummary:true (+ reviewedByName) junto con el contenido
  // final — nunca lo dispara el auto-save normal (siempre lo llama sin
  // argumentos).
  const save = async (extraFields = {}) => {
    try {
      dispatch({ type: 'SAVING' })
      let saving = Date.now()
      mutate({ ...state.record, ...extraFields }, {
        onSuccess: record => {
          dispatch({ type: 'SAVED', record, saving })
          if (props.onSave) props.onSave([record])
        },
        onError: error => {
          dispatch({ type: 'SAVE_ERROR', error })
        }
      })
    } catch (error) {
      console.error('Error saving', error)
      dispatch({ type: 'SAVE_ERROR', error })
    }
  }

  // No reusa `save()` a propósito (a diferencia del resto de los guardados
  // de este componente): sin un `approved` propio, el próximo render volvía
  // a mostrar "Guardar y aprobar" en vez de "Guardado" — ver el comentario
  // de `approved` más arriba. `save()` no tiene forma de enterarse del
  // éxito PUNTUAL de esta mutation en particular (su success handler es
  // genérico, para el auto-save de cualquier registro).
  //
  // Mismo criterio ya usado para createdByName (más arriba, initialRecord) —
  // el label del PROFESIONAL (perfil clínico), no de la cuenta logueada, que
  // puede ser una secretaria operando por otro profesional.
  const approveAiSummary = () => {
    dispatch({ type: 'SAVING' })
    const saving = Date.now()
    mutate({
      ...state.record,
      approveAiSummary: true,
      reviewedByName: props.resource?.label || props.user?.name || null,
    }, {
      onSuccess: record => {
        dispatch({ type: 'SAVED', record, saving })
        setApproved(true)
        if (props.onSave) props.onSave([record])
        // Pedido de Juan Cruz (QA, 2026-08-20): un resumen aprobado es un
        // "listo, siguiente" — a diferencia de una evolución escrita a mano
        // (que puede seguir editándose indefinidamente), cerrar el modo
        // edición evita que el profesional lo abra de nuevo por accidente y
        // dispare un auto-save sobre contenido que ya se aprobó.
        if (props.onApproved) props.onApproved()
      },
      onError: error => dispatch({ type: 'SAVE_ERROR', error }),
    })
  }

  const hideModals = () => {
    setShowErrorModal(false)
    setShowNewEvolutionModal(false)
    setShowNavigationModal(false)
  }

  const saveAndCreateNew = async () => {
    // verificar si ya hay un guardado en progreso
    const isSaving = mutation.isLoading || (typeof state.saving === 'number')

    if (isSaving) {
      // si ya se esta guardando, marcar la accion como pendiente
      setPendingActionAfterSave(() => () => {
        hideModals()
        setTimeout(() => {
          createNewRecord();
        }, 200)
      })
      return
    }

    dispatch({ type: 'SAVING' })
    let saving = Date.now()
    mutate(state.record, {
      onSuccess: record => {
        dispatch({ type: 'SAVED', record, saving })
        if (props.onSave) props.onSave([record])

        hideModals()
        setTimeout(() => {
          createNewRecord();
        }, 200)
      },
      onError: error => {
        dispatch({ type: 'SAVE_ERROR', error })
        hideModals()
        setPendingActionAfterSave(null)
      }
    })
  }

  const handleNavigateWithoutSaving = () => {
    setShowNavigationModal(false)
    setShowErrorModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const saveAndNavigateFromError = async () => {
    const isSaving = mutation.isLoading || (typeof state.saving === 'number')

    if (isSaving) {
      setPendingActionAfterSave(() => () => {
        setShowErrorModal(false)
        if (pendingNavigation) {
          pendingNavigation()
          setPendingNavigation(null)
        }
      })
      return
    }

    dispatch({ type: 'SAVING' })
    let saving = Date.now()
    mutate(state.record, {
      onSuccess: record => {
        dispatch({ type: 'SAVED', record, saving })
        if (props.onSave) props.onSave([record])

        setShowErrorModal(false)
        if (pendingNavigation) {
          pendingNavigation()
          setPendingNavigation(null)
        }
      },
      onError: error => {
        dispatch({ type: 'SAVE_ERROR', error })
        setShowErrorModal(false)
        setPendingNavigation(null)
        setPendingActionAfterSave(null)
      }
    })
  }

  const onNewEvolution = async () => {
    const isSaved = state.saving === false && !state.changed && !state.error
    const hasError = state.error || mutation.error
    const isSaving = mutation.isLoading || (typeof state.saving === 'number')
    const hasContent = state.record.content && state.record.content.trim().length > 0

    if (isSaving) {
      setShowNewEvolutionModal(true)
      return
    }

    if (hasError) {
      setShowErrorModal(true)
      return
    }

    if (!isSaved && hasContent) {
      setShowNewEvolutionModal(true)
      return
    }

    createNewRecord()
  }

  const createNewRecord = () => {
    const newRecord = {
      consumer: { id: props.consumer?.id, label: props.consumer?.label },
      createdBy: props.user?.uid,
      createdByName: props.resource?.label || props.user?.name,
      createdByResourceID: props.resource?.id,
      type: 'records',
      date: getDate()
    }
    dispatch({ type: 'NEW_RECORD', record: newRecord })
    setEditorKey(prev => prev + 1)
  }


  return (
    <>
      <div className="mb-3 d-print-none" style={{ background: '#ffef5f69' }}>
        <div>
          {!!props.tabs && (
            // Bug real (auditoría 2026-08-24, responsive): hasta 9 botones
            // (+Evolución, Archivos, +Diagnostico, +Tratamiento, +Signos
            // Vitales, Receta, Orden, +Labs, +Formularios) en una sola fila
            // sin flex-wrap ni scroll propio — en mobile desbordaba la
            // pantalla entera en vez de acomodarse.
            //
            // Bug real (Trello "V2 | Arreglos de Responsive Design",
            // 2026-09-04): el flex-wrap de arriba resolvía el desborde
            // bajando de línea, pero con muchas opciones cargadas (labs +
            // formularios + Trial) terminaba en 2-3 filas, cambiando la
            // altura de este bloque según el paciente. Se reemplaza por un
            // único renglón con scroll lateral (flex-nowrap + overflow-x)
            // — mismo criterio que la barra de tabs de secciones más abajo
            // (ver EHR.js). Las funcionalidades Trial (trialTools/
            // trialToolsEnd) se agrupan en un renglón APARTE debajo, con el
            // mismo mecanismo — no son parte del flujo de "crear una
            // entrada nueva" de este renglón.
            //
            // Bug real (Trello "V2 | Arreglos de Responsive Design",
            // seguimiento 2026-09-08, Juan Cruz QA): la clase Bootstrap
            // `overflow-x-auto` NO EXISTE en bootstrap@5.2.0 (package.json)
            // — recién se agregó en 5.3. La clase no hacía nada, el
            // contenido excedente se pintaba superpuesto sobre lo que
            // venía después en vez de scrollear. Se pasa a overflowX
            // inline, que sí existe.
            <div className="d-flex align-items-center flex-nowrap gap-2 pb-1" style={{ overflowX: "auto" }}>
              <div>
                <button
                  className="py-1 px-2 btn btn-sm btn-primary ms-1"
                  onClick={onNewEvolution}
                >
                  {t('ehr:new_evolution', '+Evolución')}
                </button>
              </div>
              <div>
                <Files
                  consumer={props.consumer}
                  resource={props.resource}
                  user={props.user}
                  onSave={props.onSave}
                />
              </div>
              <div>
                <button className="py-2 px-3 btn btn-sm btn-link" data-bs-toggle="dropdown" data-form="diagnostic" data-bs-auto-close="outside">{t('ehr:diagnostic', '+ Diagnostico', { context: 'form' })}</button>
                <div className="dropdown-menu mt-1 p-2 bg-white border shadow-sm rounded" style={{ maxWidth: 320, minWidth: 320, width: 320 }}>
                  {show.diagnostic && (
                    <Diagnostic
                      consumer={props.consumer}
                      resource={props.resource}
                      user={props.user}
                      onSave={onSave('diagnostic')}
                      onClose={() => onClose('diagnostic')}
                    />
                  )}
                </div>
              </div>
              <div>
                <button
                  className="py-2 px-3 btn btn-sm btn-link"
                  data-bs-toggle="dropdown"
                  data-form="treatment"
                  data-bs-auto-close="outside"
                >{t('ehr:treatment', '+ Tratamiento', { context: 'form' })}</button>
                <div className="dropdown-menu mt-1 p-2 bg-white border shadow-sm rounded" style={{ maxWidth: 320, minWidth: 320, width: 320 }}>
                  {show.treatment && (
                    <Treatment
                      consumer={props.consumer}
                      resource={props.resource}
                      user={props.user}
                      onSave={onSave('treatment')}
                      onClose={() => onClose('treatment')}
                    />
                  )}
                </div>
              </div>

              <div>
                <button className="py-2 px-3 btn btn-sm btn-link" data-bs-toggle="dropdown" data-form="vitalSigns" data-bs-auto-close="outside">{t('ehr:vitalSigns', '+ Signos Vitales', { context: 'form' })}</button>
                <div className="dropdown-menu mt-1 p-2 bg-white border shadow-sm rounded" style={{ maxWidth: 320, minWidth: 320, width: 320 }}>
                  {show.vitalSigns && (
                    <VitalSigns
                      consumer={props.consumer}
                      resource={props.resource}
                      user={props.user}
                      onSave={onSave('vitalSigns')}
                      onClose={() => onClose('vitalSigns')}
                    />
                  )}
                </div>
              </div>

              <div>
                <button
                  className="py-2 px-3 btn btn-sm btn-link"
                  onClick={() => setShow({ ...show, prescription: true })}
                >{t('ehr:prescription', 'Receta', { context: 'form' })}</button>
                {show.prescription && (
                  <div className="position-fixed top-0 start-0 end-0 bottom-0 w-100 h-100 overflow-auto" style={{ zIndex: 1020, background: 'rgba(0, 0, 0, 0.8)' }}>
                    <Prescription
                      consumer={props.consumer}
                      resource={props.resource}
                      user={props.user}
                      type="prescription"
                      onSave={prescription => props.onSave([prescription], 'prescriptions:update')}
                      onClose={() => setShow({ ...show, prescription: false })}
                    />
                  </div>
                )}
              </div>

              <div>
                <button
                  className="py-2 px-3 btn btn-sm btn-link"
                  onClick={() => setShow({ ...show, medicalOrder: true })}
                >{t('ehr:medical_order', 'Orden', { context: 'form' })}</button>
                {show.medicalOrder && (
                  <div className="position-fixed top-0 start-0 end-0 bottom-0 w-100 h-100 overflow-auto" style={{ zIndex: 1020, background: 'rgba(0, 0, 0, 0.8)' }}>
                    <Prescription
                      consumer={props.consumer}
                      resource={props.resource}
                      user={props.user}
                      type="order"
                      onSave={prescription => props.onSave([prescription], 'prescriptions:update')}
                      onClose={() => setShow({ ...show, medicalOrder: false })}
                    />
                  </div>
                )}
              </div>

              {props.labs && (
                <div>
                  <button className="py-2 px-3 btn btn-sm btn-link" data-bs-toggle="dropdown" data-form="labs" data-bs-auto-close="outside">{t('ehr:labs', '+ Labs', { context: 'form' })}</button>
                  <div className="dropdown-menu mt-1 p-2 bg-white border shadow-sm rounded" style={{ maxWidth: 320, minWidth: 320, width: 320 }}>
                    {show.labs && (
                      <div className="lh-1">
                        <div className="mb-1"><small className="text-secondary">{t('ehr:v2_labs_message', 'Laboratorios se debe usar desde la version anterior. Estamos trabajando para que se pueda usar desde aca', { context: 'form' })}</small></div>
                        <small><a href={`${location.pathname.replace('v2', 'labs')}?mode=static`} rel="noopener noreferrer" target={`labs${props.consumer.id.replace('consumers/', '')}`}>{t('ehr:v2_labs_link', 'Ir a la version anterior (se abre en una nueva pestaña)')}</a></small>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {props.forms && (
                <div>
                  <button className="py-2 px-3 btn btn-sm btn-link" data-bs-toggle="dropdown" data-form="forms" data-bs-auto-close="outside">{t('ehr:forms', '+ Formularios', { context: 'form' })}</button>
                  <div className="dropdown-menu mt-1 p-2 bg-white border shadow-sm rounded" style={{ maxWidth: 320, minWidth: 320, width: 320 }}>
                    {show.forms && (
                      <div className="lh-1">
                        <div className="mb-1"><small className="text-secondary">{t('ehr:v2_forms_message', 'Los formularios se deben usar desde la version anterior. Estamos trabajando para que se pueda usar desde aca', { context: 'form' })}</small></div>
                        <small><a href={`${location.pathname.replace('v2', 'forms')}?mode=static`} rel="noopener noreferrer" target={`forms${props.consumer.id.replace('consumers/', '')}`}>{t('ehr:v2_labs_link', 'Ir a la version anterior (se abre en una nueva pestaña)')}</a></small>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Bug real (Trello "V2 | Arreglos de Responsive Design",
              2026-09-04): las pills de features Trial (Odontograma/
              Percentiles, reubicación 2026-09-01 pedido de Juan Cruz) y
              Escriba ("Grabar consulta") vivían mezcladas en el mismo
              renglón que los botones de creación real (Evolución/Archivos/
              Diagnóstico/etc.) — se agrupan en su propio renglón, debajo,
              con el mismo scroll lateral. Escriba sigue yendo AL FINAL de
              este renglón (mismo criterio que el comentario original: en
              flujo normal, no position:fixed/absolute, para no superponerse
              con el editor/la fila de guardado de más abajo). */}
          {!!props.tabs && (props.trialTools || props.trialToolsEnd) && (
            // overflow-x-auto (Bootstrap) no existe en 5.2 — ver comentario
            // en el renglón de creación, unos renglones más arriba.
            //
            // Bug real (seguimiento 2026-09-10, Juan Cruz QA): a diferencia
            // del renglón de creación de arriba (su primer botón lleva
            // ms-1), este renglón no tenía ningún margen izquierdo — el
            // borde de la primera pill (Odontograma/Percentiles, fondo
            // blanco) quedaba pegado al límite del contenedor padre (fondo
            // amarillo, mb-3), dando la sensación de que se superponían.
            <div className="d-flex align-items-center flex-nowrap gap-2 pb-1 mt-2 ms-1" style={{ overflowX: "auto" }}>
              {props.trialTools}
              {props.trialToolsEnd}
            </div>
          )}

          <div>
            <Editor
              key={editorKey}
              value={state.record.content}
              placeholder={t("ehr:record_placeholder", "Escriba aqui la evolucion")}
              onEditorChange={onEditorChange}
            />
          </div>

          <div className="d-flex align-items-center">
            <div className="me-3">
              <input
                style={{ maxWidth: '118px' }}
                type="date" className="form-control form-control-sm p-2 border-0 w-auto bg-transparent" value={state.record.date} onChange={onChangeDate}
              />
            </div>
            {isUnreviewedAiSummary ? (
              // Sin auto-save: el único guardado posible en este estado es
              // explícito, vía este botón — ver el comentario largo junto al
              // useEffect del auto-save, más arriba.
              state.error ? (
                <div className="p-2 d-flex align-items-center gap-2">
                  <small className="text-danger">{t('ehr:record_save_error', 'Error al guardar')}</small>
                  <button type="button" className="btn btn-sm btn-primary" onClick={approveAiSummary} disabled={mutation.isLoading}>
                    {mutation.isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        {t('ehr:record_is_saving', 'Guardando...')}
                      </>
                    ) : (
                      t('ehr:approve_ai_summary', 'Guardar y aprobar')
                    )}
                  </button>
                </div>
              ) : mutation.isLoading ? (
                // [Bug real encontrado escribiendo el test de este botón] a
                // diferencia del resto de los usos de state.saving en este
                // componente, acá NO alcanza con chequear "typeof state.saving
                // === 'number'": ese valor lo pisa CUALQUIER edición (ver
                // reducer, case SET_RECORD) esperando a que el auto-save
                // debounceado lo resuelva a los 2s — pero para un resumen sin
                // revisar ese auto-save nunca se dispara a propósito. Si se
                // usara ese chequeo, escribir una sola letra escondería este
                // botón detrás de un "Guardando..." que jamás se resuelve,
                // dejando al profesional sin ninguna forma de aprobar. La
                // única señal real de un guardado en curso acá es
                // mutation.isLoading (el mutate() disparado por este mismo
                // botón).
                <div className="p-2">
                  <small className="text-secondary">{t('ehr:record_is_saving', 'Guardando...')}</small>
                </div>
              ) : (
                <button type="button" className="btn btn-sm btn-primary" onClick={approveAiSummary}>
                  {t('ehr:approve_ai_summary', 'Guardar y aprobar')}
                </button>
              )
            ) : state.error ? (
              showErrorSpinner ? (
                <div className="p-2">
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  <small className="text-secondary">{t('ehr:record_is_saving', 'Guardando...')}</small>
                </div>
              ) : (
                <div className="p-2 d-flex align-items-center gap-2">
                  <small className="text-danger">{t('ehr:record_save_error', 'Error al guardar')}</small>
                  <div
                    type="button"
                    className="small text-decoration-underline fw-bold"
                    onClick={save}
                    disabled={mutation.isLoading}
                  >
                    {mutation.isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        {t('ehr:record_is_saving', 'Guardando...')}
                      </>
                    ) : (
                      t('save', 'Guardar')
                    )}
                  </div>
                </div>
              )
            ) : mutation.isLoading || (state.saving && typeof state.saving === 'number') ? (
              <div className="p-2">
                <small className="text-secondary">{t('ehr:record_is_saving', 'Guardando...')}</small>
              </div>
            ) : state.saving !== undefined ? (
              <div className="p-2">
                <small className="text-success">{t('ehr:record_no_changes', 'Guardado')}</small>
              </div>
            ) : null}
          </div>

        </div>
      </div>

      <Modal show={showErrorModal} size="md" onHide={() => setShowErrorModal(false)} dialogClassName='font-nunito modal-fullscreen-sm-down'
        style={{ maxHeight: "80vh" }}
      >
        <Modal.Header className='border-0'>
          {t('ehr:new_evolution_save_error', "Hubo un error al guardar la evolucion. ¿Quieres intentar guardar antes de continuar?")}
        </Modal.Header>
        <Modal.Body>
        </Modal.Body>
        <Modal.Footer className='border-0'>
          <button
            className="btn btn-primary"
            onClick={() => pendingNavigation ? saveAndNavigateFromError() : saveAndCreateNew()}
            disabled={mutation.isLoading || (typeof state.saving === 'number')}
          >
            {mutation.isLoading || (typeof state.saving === 'number') ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {t('ehr:record_is_saving', 'Guardando...')}
              </>
            ) : (
              t('ehr:save', "Guardar")
            )}
          </button>
          <button className="btn btn-secondary" onClick={() => {
            setShowErrorModal(false)
            if (pendingNavigation) {
              handleNavigateWithoutSaving()
            }
          }}>
            {pendingNavigation ? t('ehr:exit_without_saving', "Salir sin guardar") : t('ehr:cancel', "Cancelar")}
          </button>
        </Modal.Footer>
      </Modal>

      <SavingModal
        show={showNavigationModal}
        onHide={() => setShowNavigationModal(false)}
        onConfirm={handleNavigateWithoutSaving}
        confirmLabel={t('ehr:exit', "Salir")}
        closeStyle={{ position: "absolute", top: "20px", right: "20px" }}
        isSaving={mutation.isLoading || (typeof state.saving === 'number')}
        t={t}
      />

      <SavingModal
        show={showNewEvolutionModal}
        onHide={() => setShowNewEvolutionModal(false)}
        onConfirm={() => { createNewRecord(); setShowNewEvolutionModal(false) }}
        confirmLabel={t('ehr:go_:back', "Volver")}
        closeStyle={{ position: "absolute", top: "20px", right: "20px" }}
        isSaving={mutation.isLoading || (typeof state.saving === 'number')}
        t={t}
      />
    </>
  )
}

export default Form
